import { useRef, useCallback } from 'react';
import { getAuthToken, getApiBaseUrl } from '../../../services/api';
import type { QueryResult, SSEErrorCode, SSEEvent } from '../types/chat.types';

interface StreamCallbacks {
  onDelta: (text: string) => void;
  onToolStart: (tool: string, input?: string) => void;
  onToolDone: (tool: string, durationMs?: number) => void;
  onDone: (result: QueryResult, sessionId?: string) => void;
  onError: (message: string, code?: SSEErrorCode) => void;
}

export function useSSEStream() {
  const abortRef = useRef<AbortController | null>(null);

  const cancel = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
  }, []);

  const startStream = useCallback(
    async (query: string, sessionId: string | undefined, callbacks: StreamCallbacks) => {
      // Cancel any existing stream
      cancel();

      const token = getAuthToken();
      if (!token) {
        callbacks.onError('Authentication token not available', 'transient');
        return;
      }

      abortRef.current = new AbortController();
      const baseUrl = getApiBaseUrl();

      try {
        const response = await fetch(`${baseUrl}/api/llm/query/stream`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            query,
            ...(sessionId ? { session_id: sessionId } : {}),
          }),
          signal: abortRef.current.signal,
        });

        if (!response.ok) {
          const errorText = await response.text();
          callbacks.onError(
            response.status === 429
              ? 'Rate limit exceeded. Please wait before sending another query.'
              : `Request failed: ${errorText}`,
            'transient'
          );
          return;
        }

        if (!response.body) {
          callbacks.onError('Streaming not supported by server', 'transient');
          return;
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let reading = true;

        while (reading) {
          const { done, value } = await reader.read();
          if (done) {
            reading = false;
            break;
          }

          buffer += decoder.decode(value, { stream: true });

          // Parse SSE events from buffer
          const lines = buffer.split('\n');
          buffer = lines.pop() || ''; // Keep incomplete line in buffer

          for (const line of lines) {
            if (line.startsWith('event: ')) {
              // Event type is also encoded in the JSON data, so we just skip the event line
              continue;
            } else if (line.startsWith('data: ')) {
              const jsonStr = line.slice(6);
              try {
                const event = JSON.parse(jsonStr) as SSEEvent;
                switch (event.type) {
                  case 'delta':
                    callbacks.onDelta(event.text);
                    break;
                  case 'tool_start':
                    callbacks.onToolStart(event.tool, event.input);
                    break;
                  case 'tool_done':
                    callbacks.onToolDone(event.tool, event.duration_ms);
                    break;
                  case 'done':
                    callbacks.onDone(event.query_result, event.session_id);
                    break;
                  case 'error':
                    callbacks.onError(event.message, event.code);
                    break;
                }
              } catch {
                // Skip malformed JSON lines
              }
            }
          }
        }
      } catch (err: unknown) {
        if (err instanceof DOMException && err.name === 'AbortError') {
          // User cancelled — not an error
          return;
        }
        callbacks.onError(
          err instanceof Error ? err.message : 'Stream connection failed',
          'transient'
        );
      }
    },
    [cancel]
  );

  return { startStream, cancel };
}
